/**
 * Privacy-safe storefront behavior events.
 *
 * Shopify's native Customer Events remain the only source of commerce events.
 * This bridge publishes an allowlisted set of custom UI events to Shopify Web
 * Pixels and, with Shopify analytics consent, the already-loaded GA4 tag. It
 * never creates a tag-manager surface or forwards arbitrary properties.
 */
(function registerBohemiaBehaviorEvents() {
  if (window.BohemiaBehaviorEvents) return;

  const NEWSLETTER_MARKER_KEY = 'bohemiaNewsletterSignupAttempt';
  const NEWSLETTER_MARKER_MAX_AGE_MS = 30 * 60 * 1000;
  const GA4_MEASUREMENT_ID = 'G-JTT9JZDXKX';
  let customerPrivacyReady = false;

  try {
    if (typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (error) return;
          customerPrivacyReady = (
            typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function'
          );
        },
      );
    }
  } catch (error) {
    // The GA4 destination remains disabled when Shopify's privacy feature cannot load.
  }

  const eventSchemas = {
    newsletter_popup_viewed: {},
    cart_drawer_opened: {},
    checkout_clicked: {
      placement: { required: true, values: ['drawer', 'cart_page'] },
    },
    newsletter_signup_succeeded: {
      placement: { required: true, values: ['popup', 'footer'] },
    },
    size_guide_opened: {
      product_id: { required: true, type: 'id' },
      placement: { required: true, values: ['variant_selector', 'size_fit_panel'] },
    },
    variant_selected: {
      product_id: { required: true, type: 'id' },
      variant_id: { required: true, type: 'id' },
    },
    product_details_opened: {
      product_id: { required: true, type: 'id' },
      panel: {
        required: true,
        values: ['product_information', 'details', 'size_fit', 'care', 'shipping_returns'],
      },
    },
    complete_look_viewed: {
      product_id: { required: true, type: 'id' },
      layout: {
        required: true,
        values: ['editorial_tile', 'slim_strip', 'coordinated_look'],
      },
      source: { required: true, values: ['complementary', 'legacy'] },
      treatment: { values: ['hibiscus-look-v1'] },
    },
    complete_look_add_clicked: {
      product_id: { required: true, type: 'id' },
      pairing_product_id: { required: true, type: 'id' },
      layout: {
        required: true,
        values: ['editorial_tile', 'slim_strip', 'coordinated_look'],
      },
      source: { required: true, values: ['complementary', 'legacy'] },
      treatment: { values: ['hibiscus-look-v1'] },
      cart_action: { required: true, values: ['add_to_bag'] },
    },
    complete_look_add_succeeded: {
      product_id: { required: true, type: 'id' },
      pairing_product_id: { required: true, type: 'id' },
      layout: {
        required: true,
        values: ['editorial_tile', 'slim_strip', 'coordinated_look'],
      },
      source: { required: true, values: ['complementary', 'legacy'] },
      treatment: { values: ['hibiscus-look-v1'] },
      cart_action: { required: true, values: ['add_to_bag'] },
    },
    cart_recommendation_add_clicked: {
      product_id: { required: true, type: 'id' },
      pairing_product_id: { required: true, type: 'id' },
      relationship: { required: true, values: ['expected', 'styled', 'discovery'] },
      relationship_mode: { values: ['matching', 'accessory', 'styled'] },
      placement: { required: true, values: ['side_panel', 'inline'] },
    },
    cart_recommendation_add_succeeded: {
      product_id: { required: true, type: 'id' },
      pairing_product_id: { required: true, type: 'id' },
      relationship: { required: true, values: ['expected', 'styled', 'discovery'] },
      relationship_mode: { values: ['matching', 'accessory', 'styled'] },
      placement: { required: true, values: ['side_panel', 'inline'] },
    },
  };

  function sanitizeEvent(name, properties) {
    const schema = eventSchemas[name];
    if (!schema) return null;

    const source = properties && typeof properties === 'object' ? properties : {};
    const safe = {};

    for (const [field, rule] of Object.entries(schema)) {
      const rawValue = source[field];
      let value = null;

      if (rule.type === 'id') {
        const numericValue = Number(rawValue);
        if (Number.isSafeInteger(numericValue) && numericValue > 0) value = numericValue;
      } else if (rule.values.includes(rawValue)) {
        value = rawValue;
      }

      if (value === null) {
        if (rule.required) return null;
        continue;
      }
      safe[field] = value;
    }

    return safe;
  }

  function publish(name, properties = {}) {
    const safeProperties = sanitizeEvent(name, properties);
    if (safeProperties === null) return false;

    try {
      if (typeof window.Shopify?.analytics?.publish === 'function') {
        window.Shopify.analytics.publish(name, safeProperties);
      }
    } catch (error) {
      // Analytics must never interrupt the storefront interaction.
    }

    try {
      const customerPrivacy = window.Shopify?.customerPrivacy;
      if (
        customerPrivacyReady
        && typeof window.gtag === 'function'
        && typeof customerPrivacy?.analyticsProcessingAllowed === 'function'
        && customerPrivacy.analyticsProcessingAllowed() === true
      ) {
        const ga4Properties = { ...safeProperties };
        if (ga4Properties.source) {
          ga4Properties.pairing_source = ga4Properties.source;
          delete ga4Properties.source;
        }
        window.gtag('event', name, {
          ...ga4Properties,
          send_to: GA4_MEASUREMENT_ID,
        });
      }
    } catch (error) {
      // Each destination is isolated so consent or GA4 failures cannot block the interaction.
    }

    return true;
  }

  function closestProductId(element) {
    return element?.closest?.('[data-analytics-product-id]')?.dataset.analyticsProductId;
  }

  function handleAnnotatedClick(event) {
    const target = event.target;

    const checkout = target?.closest?.('[data-analytics-checkout-placement]');
    if (checkout) {
      publish('checkout_clicked', {
        placement: checkout.dataset.analyticsCheckoutPlacement,
      });
      return;
    }

    const sizeGuide = target?.closest?.('[data-analytics-size-guide-placement]');
    if (sizeGuide) {
      publish('size_guide_opened', {
        product_id: closestProductId(sizeGuide),
        placement: sizeGuide.dataset.analyticsSizeGuidePlacement,
      });
      return;
    }

    const productDetails = target?.closest?.('[data-analytics-product-details-panel]');
    const productDetailsAlreadyOpen = productDetails && (
      productDetails.getAttribute('aria-expanded') === 'true' ||
      productDetails.getAttribute('aria-selected') === 'true'
    );
    if (productDetails && !productDetailsAlreadyOpen) {
      publish('product_details_opened', {
        product_id: closestProductId(productDetails),
        panel: productDetails.dataset.analyticsProductDetailsPanel,
      });
    }
  }

  function newsletterPlacementForForm(form) {
    if (form?.id === 'newsletter-popup-form') return 'popup';
    if (form?.id === 'footer-newsletter-form') return 'footer';
    return null;
  }

  function markNewsletterAttempt(event) {
    const placement = newsletterPlacementForForm(event.target);
    if (!placement) return;
    try {
      sessionStorage.setItem(NEWSLETTER_MARKER_KEY, JSON.stringify({
        placement,
        submittedAt: Date.now(),
      }));
    } catch (error) {
      // Storage restrictions must not block the native Shopify form.
    }
  }

  function readNewsletterAttempt() {
    try {
      const marker = JSON.parse(sessionStorage.getItem(NEWSLETTER_MARKER_KEY) || 'null');
      const age = Date.now() - Number(marker?.submittedAt);
      if (
        !['popup', 'footer'].includes(marker?.placement)
        || !Number.isFinite(age)
        || age < 0
        || age > NEWSLETTER_MARKER_MAX_AGE_MS
      ) {
        sessionStorage.removeItem(NEWSLETTER_MARKER_KEY);
        return null;
      }
      return marker;
    } catch (error) {
      try {
        sessionStorage.removeItem(NEWSLETTER_MARKER_KEY);
      } catch (storageError) {
        // Nothing else to clean up when session storage is unavailable.
      }
      return null;
    }
  }

  function handleNewsletterResult() {
    const marker = readNewsletterAttempt();
    if (!marker) return;

    const success = document.querySelector(
      `[data-analytics-newsletter-success="${marker.placement}"]`
    );
    const error = document.querySelector(
      `[data-analytics-newsletter-error="${marker.placement}"]`
    );

    if (success) {
      publish('newsletter_signup_succeeded', { placement: marker.placement });
    }
    if (!success && !error) return;

    try {
      sessionStorage.removeItem(NEWSLETTER_MARKER_KEY);
    } catch (storageError) {
      // The event is already handled; storage cleanup is best-effort.
    }
  }

  function handleCompleteLookUi(event) {
    const detail = event.detail || {};
    const eventName = {
      view: 'complete_look_viewed',
      add_attempt: 'complete_look_add_clicked',
      add_success: 'complete_look_add_succeeded',
    }[detail.action];
    if (!eventName) return;

    publish(eventName, {
      product_id: detail.productId,
      pairing_product_id: detail.pairingProductId,
      layout: detail.layout,
      source: detail.source,
      treatment: detail.treatment,
      cart_action: detail.cartAction,
    });
  }

  window.BohemiaBehaviorEvents = Object.freeze({ publish });
  document.addEventListener('click', handleAnnotatedClick, true);
  document.addEventListener('submit', markNewsletterAttempt, true);
  document.addEventListener('bohemia:complete-look-ui', handleCompleteLookUi);
  window.addEventListener('load', handleNewsletterResult, { once: true });
})();
